import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, linkedSignal, signal } from '@angular/core';

import { buildInboxSeed } from '@common/data';
import { ANONYMOUS_PREFERENCES_OWNER, INBOX_READ_KEY_PREFIX } from '@common/enums';
import type { InboxItem } from '@common/types';

import { CurrentUserService } from './current-user.service';
import { NotificationPreferencesService } from './notification-preferences.service';

/**
 * The notification centre behind the topbar bell: in-app notifications, newest first, with read
 * state. What appears here follows the person's in-app choices, so turning a channel off in their
 * settings has a visible effect rather than only changing a stored flag.
 */
@Injectable({ providedIn: 'root' })
export class NotificationInboxService {
  private readonly document = inject(DOCUMENT);
  private readonly currentUser = inject(CurrentUserService);
  private readonly preferences = inject(NotificationPreferencesService);

  private readonly items = signal<ReadonlyArray<InboxItem>>(buildInboxSeed());

  private readonly owner = computed<string>(
    () => this.currentUser.sub() ?? ANONYMOUS_PREFERENCES_OWNER,
  );

  /** Item id to the moment it was read. */
  private readonly readAt = linkedSignal<string, Readonly<Record<string, string>>>({
    source: this.owner,
    computation: (owner) => this.load(owner),
  });

  readonly visible = computed<ReadonlyArray<InboxItem>>(() => {
    const read = this.readAt();
    return this.items()
      .filter((item) => this.preferences.receivesInApp(item.event))
      .map((item) => ({ ...item, readAt: item.readAt ?? read[item.id] ?? null }))
      .sort((a, b) => b.atIso.localeCompare(a.atIso));
  });

  readonly unreadCount = computed<number>(
    () => this.visible().filter((item) => item.readAt === null).length,
  );

  constructor() {
    effect(() => {
      const owner = this.owner();
      const read = this.readAt();
      if (owner === ANONYMOUS_PREFERENCES_OWNER) return;
      try {
        this.document.defaultView?.localStorage.setItem(storageKeyFor(owner), JSON.stringify(read));
      } catch {}
    });
  }

  receive(item: InboxItem): void {
    if (!this.items().some((existing) => existing.id === item.id))
      this.items.update((items) => [item, ...items]);
  }

  markRead(id: string): void {
    if (this.readAt()[id]) return;
    const now = new Date().toISOString();
    this.readAt.update((read) => ({ ...read, [id]: now }));
  }

  markAllRead(): void {
    const now = new Date().toISOString();
    const unread = this.visible().filter((item) => item.readAt === null);
    if (!unread.length) return;
    this.readAt.update((read) => ({
      ...read,
      ...Object.fromEntries(unread.map((item) => [item.id, now])),
    }));
  }

  private load(owner: string): Readonly<Record<string, string>> {
    if (owner === ANONYMOUS_PREFERENCES_OWNER) return {};
    try {
      const raw = this.document.defaultView?.localStorage.getItem(storageKeyFor(owner));
      const parsed: unknown = raw ? JSON.parse(raw) : {};
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return Object.fromEntries(
        Object.entries(parsed).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string',
        ),
      );
    } catch {
      return {};
    }
  }
}

function storageKeyFor(owner: string): string {
  return `${INBOX_READ_KEY_PREFIX}:${owner}`;
}
